import { createTheme, Theme } from '@mui/material/styles';
import { ColorPalette } from '../api/types';
import { DEFAULT_COLOR_PALETTE } from '../contexts/ColorContext';

// Create a theme factory that accepts dynamic colors
export const createDynamicTheme = (colors: ColorPalette = DEFAULT_COLOR_PALETTE): Theme => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: colors.primary,
        light: lightenColor(colors.primary, 20),
        dark: darkenColor(colors.primary, 15),
        50: lightenColor(colors.primary, 45),
        100: lightenColor(colors.primary, 40),
        200: colors.border || lightenColor(colors.primary, 30),
        300: lightenColor(colors.primary, 25),
        400: lightenColor(colors.primary, 20),
        500: colors.primary,
        600: darkenColor(colors.primary, 10),
        700: colors.secondary,
        800: colors.foregroundColor,
      },
      secondary: {
        main: colors.secondary,
        light: lightenColor(colors.secondary, 15),
        dark: darkenColor(colors.secondary, 15),
      },
      background: {
        default: colors.background,
        paper: lightenColor(colors.primary, 40),
      },
      text: {
        primary: colors.foregroundColor,
        secondary: colors.muted || darkenColor(colors.primary, 10),
      },
      error: {
        main: colors.destructive || DEFAULT_COLOR_PALETTE.destructive!,
      },
      success: {
        main: colors.success || colors.primary,
      },
      warning: {
        main: colors.warning || DEFAULT_COLOR_PALETTE.warning!,
      },
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"SF Pro Text"',
        '"Segoe UI"',
        'Roboto',
        'Helvetica',
        'Arial',
        'sans-serif',
      ].join(','),
      fontSize: 14,
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '14px',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: 600,
          },
          contained: {
            boxShadow: `0 2px 8px ${hexToRgba(colors.primary, 0.3)}`,
            '&:hover': {
              boxShadow: `0 4px 12px ${hexToRgba(colors.primary, 0.4)}`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: `0 4px 20px ${hexToRgba(colors.primary, 0.08)}`,
            border: `1px solid ${hexToRgba(colors.primary, 0.05)}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': { borderRadius: 8 },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 16 },
        },
      },
    },
  });
};

// Default theme using default colors
export const theme = createDynamicTheme();

// Utility functions
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, ((num >> 16) & 0xff) + amt);
  const G = Math.min(255, ((num >> 8) & 0xff) + amt);
  const B = Math.min(255, (num & 0xff) + amt);
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}

function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, ((num >> 16) & 0xff) - amt);
  const G = Math.max(0, ((num >> 8) & 0xff) - amt);
  const B = Math.max(0, (num & 0xff) - amt);
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
}

function hexToRgba(hex: string, alpha: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
 